/**
 * Firebase Session Management Utilities
 * Handles token validation and session checks
 */

import { auth } from '../firebase';

/**
 * Check if Firebase user session is still valid
 * @returns {Promise<string|null>} Returns valid token or null if invalid
 */
export const getValidFirebaseToken = async () => {
  try {
    if (!auth?.currentUser) {
      return null;
    }

    // Get fresh token
    const token = await auth.currentUser.getIdToken(true);
    return token;
  } catch (error) {
    console.error('Failed to get valid Firebase token:', error);
    return null;
  }
};

/**
 * Verify if current stored token is still valid
 * @returns {Promise<boolean>} True if token is valid
 */
export const isTokenValid = async () => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return false;
  }

  try {
    // Try to get a new token - will fail if user session expired
    const newToken = await getValidFirebaseToken();
    if (newToken) {
      // Update stored token if we got a fresh one
      localStorage.setItem('token', newToken);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Token validation failed:', error);
    return false;
  }
};

/**
 * Get current valid token or refresh it
 * @returns {Promise<string|null>}
 */
export const refreshToken = async () => {
  try {
    const newToken = await getValidFirebaseToken();
    if (newToken) {
      localStorage.setItem('token', newToken);
      return newToken;
    }
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
};

/**
 * Lightweight JWT expiry check (no network required)
 * @param {string} token - JWT string
 * @returns {boolean} true if expired or invalid
 */
export const isTokenExpired = (token) => {
  if (!token || typeof token !== 'string') return true;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    const exp = payload?.exp;
    if (!exp) return false; // No exp claim; treat as non-expiring

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return exp <= nowInSeconds;
  } catch (error) {
    console.warn('Failed to decode token for expiry check:', error);
    return true;
  }
};
